<template>
    <div
        class="rounded border bg-base-100 border-base-300 shadow-lg p-2 flex flex-col h-full"
        :class="'genleg-grid-' + ledger.accountNo"
    >
        <div class="flex items-baseline gap-2">
            <account-link :account-no="ledger.accountNo" class="text-primary text-lg" data-cy="account-link">
                {{ ledger.accountNo }}
            </account-link>
            <span class="text-lg" data-cy="account-name">
                {{ accountName }}
            </span>
        </div>
        <div class="flex flex-col h-full">
            <ag-grid-vue
                :pinned-top-row-data="pinnedTopRowData"
                :pinned-bottom-row-data="pinnedBottomRowData"
                :datasource="tlDatasource"
                :grid-options="gridOptions"
                :theme="agTheme"
                row-model-type="infinite"
                :column-defs="colDefs"
                class="h-full grow"
            />
        </div>
    </div>
</template>

<script setup lang="ts">

import {computed, inject, PropType, Ref, shallowRef} from 'vue'
import AccountLink from '@/bookkeeping-account/AccountLink.vue'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {ColDef, GetContextMenuItemsParams, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IDatasource, IGetRowsParams, RowClassParams, ValueGetterParams} from 'ag-grid-community'
import {getTransactionLines} from '@/transaction/transaction.service'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {Filters} from '@/query/Filters'
import {type GeneralLedgerColumn} from '@/genleg/GeneralLedgerColumn'
import {type GenLegQuery} from '@/genleg/GenLegQuery'
import GenLegTransNameCell from '@/genleg/GenLegTransNameCell.vue'
import GenLegDescriptionCell from '@/genleg/GenLegDescriptionCell.vue'
import {useVatService} from '@/vat/vat.service'
import {globalDefaultColDef} from '@/agUtils'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'
import GenLegAttachmentCell from '@/genleg/GenLegAttachmentCell.vue'

const {getVatOptionById} = useVatService()

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')

const props = defineProps({
    accountNo: {
        type: String,
        required: true
    },
    ledger: {
        type: Object as PropType<LedgerAccount>,
        required: true
    },
    query: {
        type: Object as PropType<GenLegQuery>,
        required: true
    },
    //TODO: Look into supplyin cross-tenant context from panel perhaps?
    overrideTenantId: {
        type: String,
        required: false,
    }
})

const gridApi = shallowRef<GridApi>()

const accountName = computed(() => {
    return findAccountByNo(props.accountNo)?.name
})

const pinnedTopRowData = computed(() => {
    return [
        {
            date: props.query.fromDate,
            uuid: 'incoming-balance',
            description: 'Inngående saldo',
            type: 'incoming-balance',
            amount: props.ledger.incomingBalance
        }
    ]
})

const pinnedBottomRowData = computed(() => {
    return [
        {
            date: props.query.toDate,
            uuid: 'outgoing-balance',
            description: 'Utgående saldo',
            type: 'outgoing-balance',
            amount: props.ledger.outgoingBalance
        },
        {
            uuid: 'sum-changes',
            description: 'Endring i perioden',
            type: 'sum-changes',
            amount: props.ledger.movement
        }
    ]
})

const tlDatasource = computed<IDatasource>(() => ({
    rowCount: props.ledger?.numTransactionLines,
    async getRows(params: IGetRowsParams) {
        const q = props.query
        const queryRequest = toQueryRequest<GeneralLedgerColumn>(params as any)
        queryRequest.filters = {
            date: Filters.and([Filters.date(q.fromDate).onOrAfter(), Filters.date(q.toDate).onOrBefore()]),
            accountNo: Filters.text(props.accountNo).equals(),
            ...(q.fullText && {fullText: Filters.text(q.fullText).equals()}),
            ...(q.project && {project: Filters.text(q.project).equals()}),
            ...(q.department && {department: Filters.text(q.department).equals()}),
        }

        const headers = props.overrideTenantId ? {tenantId: props.overrideTenantId} : undefined
        const tls = await getTransactionLines(queryRequest, headers)
        params.successCallback(tls, props.ledger?.numTransactionLines)
    }
}))

const showGenLegReportExportsDialog = () => {
    showExportsDialog(ReportType.GENLEG, {fromDate: props.query.fromDate, toDate: props.query.toDate})
}

const gridOptions: GridOptions = {
    getRowId: ({data}: { data: TransactionLine }) => data.uuid,
    onGridReady(event: GridReadyEvent) {
        gridApi.value = event.api
    },
    enableCellTextSelection: true,
    getRowClass: (params: RowClassParams) => {
        if (['incoming-balance', 'outgoing-balance', 'sum-changes'].includes(params.data?.type)) {
            return '!bg-base-200'
        } else {
            return '!bg-base-100'
        }
    },
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: false,
        filter: false
    },
    context: {
        panelId,
        panelPathSegment
    },
    popupParent: document.body,
    getContextMenuItems: (params: GetContextMenuItemsParams) => {
        const items = (params.defaultItems || []).filter(item => item !== 'export')

        return [
            ...items,
            {
                name: 'Eksporter...',
                icon: '<span class="ag-icon ag-icon-save" />',
                action: showGenLegReportExportsDialog
            }
        ]
    }
}

const colDefs: ColDef[] = [
    {
        headerName: 'Dato',
        width: 100,
        field: 'date',
        cellRenderer: ({value}: { value: string }) => formattedDate(value),
        sortable: true,
        sort: 'asc',
    },
    {
        headerName: 'Bilag',
        width: 100,
        field: 'transactionName',
        cellRenderer: GenLegTransNameCell,
        contextMenuItems: []
    },
    {
        headerName: 'Beskrivelse',
        flex: 2,
        width: 600,
        field: 'description',
        cellRenderer: GenLegDescriptionCell,
        contextMenuItems: []
    },
    {
        headerName: 'Vedlegg',
        field: 'attachment',
        width: 200,
        minWidth: 70,
        flex: 1,
        cellRenderer: GenLegAttachmentCell,
        contextMenuItems: []
    },
    {
        headerName: 'MVA',
        width: 90,
        valueGetter: ({data}: ValueGetterParams) => {
            const vatId = data?.vatOptionId
            if (vatId > 1) {
                const vatOption = getVatOptionById(vatId)
                return vatOption?.code
            }
        }
    },
    {
        headerName: 'Prosjekt',
        width: 150,
        field: 'project',
    },
    {
        headerName: 'Avdeling',
        width: 150,
        field: 'department',
    },
    {
        headerName: 'Beløp',
        width: 120,
        field: 'amount',
        headerClass: 'header-right',
        cellClass: 'text-right header-right',
        cellClassRules: {
            'font-bold': ({data}: { data: any }) => {
                return ['incoming-balance', 'outgoing-balance', 'movement'].includes(data?.type)
            },
            'text-red-500 line-through': ({data}: { data: any }) => {
                return data?.deleted
            }
        },
        cellRenderer: ({value}: ICellRendererParams) => formatCurrency(value),
        filter: 'agNumberColumnFilter',
        sortable: true,
        cellStyle: {overflow: 'visible'},

    }
]

</script>

<style>

</style>